// v49.45 — MobileShellHeader.
// 52px sticky slim header for the mobile shell. Ported from v48.109.
// Carries: LC mark + path-aware page title (left), search button +
// bell-with-unread-badge (right). Backdrop-blur background, 3px
// AI-gradient accent strip below, safe-area-top padding so it sits
// flush under the iOS notch.

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useShellUser } from "@/features/auth/AuthContext";
import { pageTitleFor } from "../nav";
import { useSidebarBadges } from "../useSidebarBadges";

interface MobileShellHeaderProps {
  onOpenSearch?: () => void;
}

export function MobileShellHeader({ onOpenSearch }: MobileShellHeaderProps = {}) {
  const { pathname } = useLocation();
  const navigate     = useNavigate();
  const user         = useShellUser();
  const badges       = useSidebarBadges();
  const title        = pageTitleFor(pathname, user?.role);
  /* v50.68 — fold the tenders action count into the mobile aggregate
     so the bell badge reflects work-waiting across all surfaces. */
  const unread       = (badges?.notifications || 0) + (badges?.inbox || 0) + (badges?.tenders || 0) + (badges?.futures || 0);

  return (
    <header
      role="banner"
      className="lc-safe-pt sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-lc-line"
      style={{ WebkitBackdropFilter: "saturate(180%) blur(14px)" }}
    >
      <div className="h-[52px] px-3 flex items-center gap-2.5">
        {/* Brand mark + title. */}
        <Link
          to="/dashboard"
          aria-label="LabourCore — home"
          className="inline-flex items-center gap-2 flex-1 min-w-0 px-1 py-1.5 rounded-lc-md hover:bg-lc-bg-2/60 transition-colors"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <div
            aria-hidden="true"
            className="w-7 h-7 rounded-lc-sm flex items-center justify-center text-white text-[12px] font-black tracking-tight shrink-0"
            style={{ background: "linear-gradient(135deg, var(--color-lc-navy) 0%, var(--color-lc-blue-dark) 100%)" }}
          >
            LC
          </div>
          <span className="font-black text-[15px] text-lc-ink leading-tight tracking-tight truncate">
            {title}
          </span>
        </Link>

        {/* Right-side actions: Search + Bell. ≥44×44 hit targets. */}
        <button
          type="button"
          onClick={() => (onOpenSearch ? onOpenSearch() : navigate("/partners"))}
          aria-label="Search the platform"
          className="w-10 h-10 rounded-full bg-lc-bg-2 text-lc-mute flex items-center justify-center shrink-0 active:bg-lc-blue-light active:text-lc-blue-dark transition-colors"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
        <button
          type="button"
          onClick={() => navigate("/notifications")}
          aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
          className="relative w-10 h-10 rounded-full bg-lc-bg-2 text-lc-mute flex items-center justify-center shrink-0 active:bg-lc-blue-light active:text-lc-blue-dark transition-colors"
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {unread > 0 && (
            <span
              aria-hidden="true"
              className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-lc-red text-white border-2 border-white text-[9px] font-extrabold flex items-center justify-center leading-none"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </div>

      {/* AI-gradient accent strip — visual continuity with the v48 design. */}
      <div
        aria-hidden="true"
        className="h-[3px]"
        style={{
          background: "linear-gradient(90deg, var(--color-lc-blue) 0%, #7C3AED 50%, var(--color-lc-blue) 100%)",
          opacity: 0.72,
        }}
      />
    </header>
  );
}
